import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Button,
  CircularProgress,
  IconButton,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Delete, ArrowBack } from '@mui/icons-material';
import { notificationsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { formatDistanceToNow } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation('notifications');
  const { currentLanguage } = useLanguage();

  const getDateLocale = () => {
    return currentLanguage === 'ru' ? ru : enUS;
  };

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(t('panel.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(
        notifications.map((notification) =>
          notification._id === id
            ? { ...notification, read: true }
            : notification
        )
      );
      toast.success(t('panel.success.markAsRead'));
    } catch (err) {
      console.error('Error marking notification as read:', err);
      toast.error(t('panel.error.markAsRead'));
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await notificationsAPI.delete(id);
      setNotifications(
        notifications.filter((notification) => notification._id !== id)
      );
      toast.success(t('panel.success.delete'));
    } catch (err) {
      console.error('Error deleting notification:', err);
      toast.error(t('panel.error.delete'));
    }
  };

  const handleDeleteAllNotifications = async () => {
    try {
      setIsDeleting(true);
      await notificationsAPI.deleteAll();
      setNotifications([]);
      toast.success(t('panel.success.deleteAll'));
    } catch (err) {
      console.error('Error deleting all notifications:', err);
      toast.error(t('panel.error.deleteAll'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      handleMarkAsRead(notification._id);
    }

    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getNotificationTypeStyle = (type) => {
    switch (type) {
      case 'success':
        return {
          borderLeft: '4px solid #4caf50',
          bgcolor: 'rgba(76, 175, 80, 0.04)',
        };
      case 'warning':
        return {
          borderLeft: '4px solid #ff9800',
          bgcolor: 'rgba(255, 152, 0, 0.04)',
        };
      case 'error':
        return {
          borderLeft: '4px solid #f44336',
          bgcolor: 'rgba(244, 67, 54, 0.04)',
        };
      case 'info':
      default:
        return {
          borderLeft: '4px solid #2196f3',
          bgcolor: 'rgba(33, 150, 243, 0.04)',
        };
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
          onClick={() => navigate('/')}
        >
          {t('common:common.actions.backHome')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1">
            {t('panel.title')}
          </Typography>
        </Box>
        {notifications.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDeleteAllNotifications}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CircularProgress size={24} />
            ) : (
              t('panel.deleteAll')
            )}
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {notifications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('panel.empty')}
          </Typography>
        </Paper>
      ) : (
        notifications.map((notification) => (
          <Paper
            key={notification._id}
            sx={{
              p: 2,
              mb: 2,
              cursor: notification.link ? 'pointer' : 'default',
              opacity: notification.read ? 0.7 : 1,
              ...getNotificationTypeStyle(notification.type),
            }}
            onClick={() => handleNotificationClick(notification)}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: notification.read ? 'normal' : 'bold' }}
                >
                  {notification.title}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {notification.message}
                </Typography>
              </Box>
              <IconButton
                color="error"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteNotification(notification._id);
                }}
                aria-label={t('panel.delete')}
              >
                <Delete />
              </IconButton>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
              pt={1}
              borderTop="1px solid rgba(0, 0, 0, 0.12)"
            >
              <Typography variant="caption" color="text.secondary">
                {notification.createdAt
                  ? formatDistanceToNow(new Date(notification.createdAt), {
                      addSuffix: true,
                      locale: getDateLocale(),
                    })
                  : t('panel.time.recent')}
              </Typography>
              {!notification.read && (
                <Typography variant="caption" color="primary">
                  {t('panel.status.unread')}
                </Typography>
              )}
            </Box>
          </Paper>
        ))
      )}
    </Container>
  );
};

export default Notifications;
